import { useMutation } from '@apollo/client';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { TimeRangeSelect } from '@eluve/blocks';
import {
  DetailsSidebar,
  Grid,
  Heading,
  Icon,
  Separator,
  SingleDateSelector,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  AppointmentPatientIdFragment,
  patientFragment,
  useAppointmentStatus,
  useExternalAppointmentId,
  useIsAppointmentReadonly,
  useLatestSuperbill,
} from '@eluve/frontend-appointment-hooks';
import { SessionTypeSelector } from '@eluve/frontend-feature-appointment';
import { VariablesOf, graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { FeatureFlagKeysLookup } from '@eluve/posthog';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import {
  FeatureFlaggedComponent,
  GoToPatientButton,
  LocationSelector,
  PatientSelector,
  useIsFeatureFlagEnabledForLocation,
  useUserName,
} from '@eluve/smart-blocks';

import { EhrDataImporter } from '../../components/EhrDataImporter';

import { AppointmentDetailsChiefComplaintReasonForVisit } from './AppointmentDetailsChiefComplaintReasonForVisit';
import { AppointmentDetailsTabs } from './AppointmentDetailsTabs';
import { SuperbillCard } from './SuperbillCard';
import { WebPrevisitInsights } from './WebPrevisitInsights';

interface AppointmentDetailsProps {
  tenantId: string;
  appointmentId: string;
}

export const AppointmentDetailsFragment = graphql(`
  fragment AppointmentDetails on Appointments @_unmask {
    __typename
    id
    startedAt
    startDate
    endDate
    locationId
    patientId
    userId
    inputLanguage
    outputLanguage
  }
`);

const UpdateAppointmentDetailsMutation = graphql(
  `
    mutation UpdateAppointmentDetails(
      $tenantId: uuid!
      $id: uuid!
      $input: AppointmentsSetInput!
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $id }
        _set: $input
      ) {
        ...AppointmentDetails
        patient {
          ...patient
        }
      }
    }
  `,
  [AppointmentDetailsFragment, patientFragment],
);

const toDate = (date?: string | null) => (date ? new Date(date) : undefined);
const dateToISOString = (date?: Date | null) =>
  date ? date.toISOString() : null;

export const AppointmentDetails: React.FC<AppointmentDetailsProps> = ({
  tenantId,
  appointmentId,
}) => {
  const { t } = useTranslation();
  const externalAppointmentId = useExternalAppointmentId();
  const isAppointmentReadonly = useIsAppointmentReadonly();
  const toast = useToast();

  const appointmentDetails = useCompleteFragment({
    fragment: AppointmentDetailsFragment,
    key: {
      id: appointmentId,
    },
  });

  const { downloadUrl, superbill } = useLatestSuperbill();
  const showArtifacts = superbill != null;

  const insightsEnabled = useIsPostHogFeatureFlagEnabled(
    FeatureFlagKeysLookup.WEB_PREVISIT_INSIGHTS,
  );

  const isAppointmentDetailsReadonly = useIsFeatureFlagEnabledForLocation(
    'APPOINTMENT_DETAILS_READONLY',
    appointmentDetails.locationId ?? '',
  );

  const isReadonly =
    isAppointmentReadonly ||
    (isAppointmentDetailsReadonly && Boolean(externalAppointmentId));

  const username = useUserName(appointmentDetails.userId);
  const status = useAppointmentStatus();

  const [updateAppointmentDetails] = useMutation(
    UpdateAppointmentDetailsMutation,
    {
      optimisticResponse: ({ input, id }) => {
        return {
          ___typename: 'mutation_root' as const,
          updateAppointmentsByPk: {
            ...appointmentDetails,
            ...input,
            startDate: input.startDate ?? appointmentDetails.startDate,
            inputLanguage: appointmentDetails.inputLanguage ?? '',
            outputLanguage: appointmentDetails.outputLanguage ?? '',
            userId: input.userId ?? appointmentDetails.userId,
            id,
            patient: null,
          },
        };
      },
      onError: () => {
        toast.error(t('appointmentPage.details.updateFailed'));
      },
      update: (_, { data }) => {
        const patientId = data?.updateAppointmentsByPk?.patientId;
        if (!patientId) {
          return;
        }

        cacheUtils.updateFragment(
          {
            fragment: AppointmentPatientIdFragment,
            key: { id: appointmentId },
          },
          (existing) => {
            if (!existing) {
              return;
            }
            return {
              ...existing,
              patientId,
            };
          },
        );
      },
    },
  );

  const handleChange = (
    input: VariablesOf<typeof UpdateAppointmentDetailsMutation>['input'],
  ) => {
    updateAppointmentDetails({
      variables: {
        tenantId,
        id: appointmentId,
        input,
      },
    });
  };

  const detailsContent = (
    <Stack gap={2}>
      <FeatureFlaggedComponent flag="APPOINTMENT_COMPLAINT_REASON_ENABLED">
        <AppointmentDetailsChiefComplaintReasonForVisit
          appointmentId={appointmentId}
          tenantId={tenantId}
        />
      </FeatureFlaggedComponent>

      <Stack gap={4} className="px-2 pt-4 lg:px-0">
        <div className="px-2 lg:px-0">
          <Heading size="xl" weight="semibold">
            {t('appointmentPage.details.appointmentHeading')}
          </Heading>
        </div>
        <Grid
          align="center"
          gap={4}
          className="grid-cols-[auto] text-sm lg:grid-cols-[max-content_1fr]"
        >
          <span className="px-2 text-text-tertiary lg:px-0">
            {t('appointmentPage.details.date')}
          </span>
          <SingleDateSelector
            disabled={isReadonly}
            date={toDate(appointmentDetails?.startDate)}
            onDateChange={(newDate) => {
              if (newDate) {
                handleChange({ startDate: dateToISOString(newDate) });
              }
            }}
            variant="subtle"
          />

          <span className="px-2 text-text-tertiary lg:px-0">
            {t('appointmentPage.details.time')}
          </span>
          <TimeRangeSelect
            disabled={isReadonly}
            date={toDate(appointmentDetails?.startDate)}
            startTime={toDate(appointmentDetails?.startDate)}
            endTime={toDate(appointmentDetails?.endDate)}
            onStartTimeChange={(newTime) => {
              if (newTime) {
                handleChange({ startDate: dateToISOString(newTime) });
              }
            }}
            onEndTimeChange={(newTime) => {
              if (newTime) {
                handleChange({ endDate: dateToISOString(newTime) });
              }
            }}
            variant="subtle"
          />

          <span className="px-2 text-text-tertiary lg:px-0">
            {t('appointmentPage.details.location')}
          </span>
          <LocationSelector
            disabled={isReadonly}
            tenantId={tenantId}
            selectedLocationId={appointmentDetails?.locationId ?? undefined}
            onLocationSelected={(location) =>
              handleChange({ locationId: location?.id ?? null })
            }
            variant="subtle"
          />

          <FeatureFlaggedComponent flag="SESSION_TYPES">
            <Text inline color="tertiary" className="mb-2 px-2 lg:px-0">
              {t('appointmentPage.details.type')}
            </Text>
            <SessionTypeSelector disabled={isReadonly} />
          </FeatureFlaggedComponent>

          <Text inline color="tertiary" className="px-2 lg:px-0">
            {t('appointmentPage.details.owner')}
          </Text>
          <Stack
            className="overflow-hidden px-2"
            title={username}
            gap={2}
            direction="horizontal"
          >
            <Icon name="User" />
            <Text className="flex-1 truncate text-left">{username}</Text>
          </Stack>
        </Grid>
      </Stack>

      {showArtifacts && (
        <>
          <Separator className="mt-4 mb-2" />
          <Stack gap={4} className="px-2 lg:px-0">
            <div className="px-2 lg:px-0">
              <Heading size="xl" weight="semibold">
                {t('appointmentPage.details.artifacts')}
              </Heading>
            </div>
            <SuperbillCard
              createdAt={superbill.createdAt}
              downloadUrl={downloadUrl}
            />
          </Stack>
        </>
      )}

      {status !== 'NOT_STARTED' && (
        <>
          <div className="flex-1" />
          <EhrDataImporter onlyShowIfError />
        </>
      )}
    </Stack>
  );

  const insightsContent = (
    // Mirrors detailsContent's wrapper spacing so switching tabs doesn't shift
    // the content edge.
    <div className="px-2 pt-4 lg:px-0">
      <WebPrevisitInsights
        appointmentId={appointmentId}
        patientId={appointmentDetails.patientId ?? undefined}
        startDate={appointmentDetails.startDate}
        status={status}
      />
    </div>
  );

  return (
    <DetailsSidebar title={t('appointmentPage.details.title')}>
      <Stack className="p-4" gap={2} align="stretch">
        <Stack direction="horizontal" align="stretch" gap={3}>
          <div className="min-w-0 flex-1">
            <PatientSelector
              disabled={isReadonly}
              tenantId={tenantId}
              onPatientSelected={(patientId) =>
                handleChange({ patientId: patientId ?? null })
              }
            />
          </div>
          {appointmentDetails.patientId && (
            <FeatureFlaggedComponent flag="PATIENTS_VIEW">
              <GoToPatientButton
                tenantId={tenantId}
                patientId={appointmentDetails.patientId}
              />
            </FeatureFlaggedComponent>
          )}
        </Stack>

        <AppointmentDetailsTabs
          insightsEnabled={insightsEnabled}
          detailsContent={detailsContent}
          insightsContent={insightsContent}
        />
      </Stack>
    </DetailsSidebar>
  );
};
